import type { JSONObject, TrelloRestResolver, TypedJSONObject } from '../types';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import { token } from '@trello/session-cookie';
import {
  MutationAddFreeTrialArgs,
  MutationAddMemberToOrgArgs,
  MutationRemoveMemberFromWorkspaceArgs,
  MutationApplyBcDiscountArgs,
  MutationUpdateOrganizationArgs,
  MutationCopyBoardToOrgArgs,
  MutationDeleteOrganizationLogoArgs,
  MutationUploadOrganizationImageArgs,
  Organization_Stats_Cards_GroupBy,
  QueryOrganizationMemberCardsArgs,
  MutationCreateOrganizationArgs,
  MutationDeleteOrganizationArgs,
  MutationToggleAutoUpgradeArgs,
  MutationAddTagArgs,
  MutationBulkDisableWorkspacePowerUpArgs,
  MutationBulkEnableWorkspacePowerUpArgs,
  OrganizationNewBillableGuestsArgs,
  Organization_StatsCardsArgs,
  OrganizationCardsArgs,
  MutationCreateOrganizationInviteSecretArgs,
  MutationDeleteOrganizationInviteSecretArgs,
  QueryOrganizationInviteSecretArgs,
} from '../generated';
import { confirmPaidAccount } from './paidAccount';
import { fetch, trelloFetch } from '@trello/fetch';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import {
  ErrorExtensionsType,
  NetworkError,
  parseNetworkError,
} from '@trello/graphql-error-handling';
import { getNetworkClient } from '../getNetworkClient';
import { Analytics } from '@trello/atlassian-analytics';
import { isActive } from '@trello/paid-account';
import { featureFlagClient } from '@trello/feature-flag-client';
import { isQueryInfo } from '../isQueryInfo';

export const organizationNewBillableGuestsResolver: TrelloRestResolver<OrganizationNewBillableGuestsArgs> = async (
  organization: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${organization.id}/newBillableGuests/${args.boardId}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Organization.newBillableGuests',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, rootNode, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const organizationOwnedPluginsResolver: TrelloRestResolver<object> = async (
  organization: {
    id: string;
  },
  args,
  context,
  info,
): Promise<TypedJSONObject> => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${organization.id}/ownedPlugins`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Organization.ownedPlugins',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if ([401, 404, 449].includes(response.status)) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, rootNode, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const organizationLabelNamesResolver: TrelloRestResolver<object> = async (
  organization: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${organization.id}/stats/labelNames/`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Organization_Stats.labelNames',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    const mappedModel =
      Object.entries(model['data'] as JSONObject)
        // Map the labelNames returned from Apollo to LabelFilterOrgLabels
        // @ts-ignore
        .map(([name, orgLabel]: [string, JSONObject]) => ({
          name,
          ...orgLabel,
        })) || [];

    return prepareDataForApolloCache(
      {
        incomplete: model.incomplete,
        stats: mappedModel,
      },
      rootNode,
      'Organization_Stats',
    );
  } catch (err) {
    console.error(err);
    return model;
  }
};

const GroupByToIdField: Record<Organization_Stats_Cards_GroupBy, string> = {
  [Organization_Stats_Cards_GroupBy.Label]: 'idLabel',
  [Organization_Stats_Cards_GroupBy.LabelName]: 'labelName',
  [Organization_Stats_Cards_GroupBy.Member]: 'idMember',
};

const assignGroupById = (
  obj: JSONObject,
  id: string,
  groupBy: Organization_Stats_Cards_GroupBy,
) => {
  const idField = GroupByToIdField[groupBy];
  return idField ? { [idField]: id, ...obj } : obj;
};

export const organizationCardStatsResolver: TrelloRestResolver<Organization_StatsCardsArgs> = async (
  organizationStats: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const {
    groupBy,
    idBoards,
    idMembers,
    idLabels,
    dueDateUntil,
    dueDateSince,
  } = args;
  const params = new URLSearchParams({
    ...(groupBy && { groupBy: groupBy }),
    ...(idBoards && idBoards.length > 0 && { idBoards: idBoards.join(',') }),
    ...(idMembers &&
      idMembers.length > 0 && { idMembers: idMembers.join(',') }),
    ...(idLabels && idLabels.length > 0 && { idLabels: idLabels.join(',') }),
    ...(dueDateUntil && { dueDateUntil }),
    ...(dueDateSince && { dueDateSince }),
    includeIds: 'overdue,upcoming',
  });
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${organizationStats.id}/stats/cards?${params.toString()}`,
  );
  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Organization_Stats.cards',
      operationName: context.operationName,
    },
  });
  const json = await response.json();
  const cards = groupBy
    ? Object.entries((json['data'] || {}) as JSONObject).map(
        // @ts-ignore
        ([key, value]: [string, JSONObject]) => {
          return assignGroupById(value, key, groupBy);
        },
      )
    : [json['data'] || {}];
  return prepareDataForApolloCache(
    {
      incomplete: json.incomplete,
      stats: cards,
    },
    rootNode,
    'Organization_Stats',
  );
};

export const organizationStatsResolver: TrelloRestResolver<object> = (
  organization: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  return prepareDataForApolloCache(
    {
      id: organization.id,
    },
    rootNode,
    'Organization',
  );
};

export const createOrganization: TrelloRestResolver<MutationCreateOrganizationArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const { visibility, ...fields } = args;
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
  };

  if (args.traceId) {
    headers['X-Trello-TraceId'] = args.traceId;
  }

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/organizations');
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      ...(fields || {}),
      ...(visibility ? { prefs_permissionLevel: visibility } : {}),
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const updateOrganization: TrelloRestResolver<MutationUpdateOrganizationArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const { orgId, visibility, ...fields } = args;
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/organizations/${orgId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...(fields || {}),
      ...(visibility ? { 'prefs/permissionLevel': visibility } : {}),
      token: context.token || token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const deleteOrganization: TrelloRestResolver<MutationDeleteOrganizationArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
  };

  if (args.traceId) {
    headers['X-Trello-TraceId'] = args.traceId;
  }

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${args.idOrganization}`,
  );
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });

  throw await parseNetworkError(response);
};

export const copyBoardToOrg: TrelloRestResolver<MutationCopyBoardToOrgArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  // Pre-fetch before mutation
  let response = await trelloFetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      credentials: 'same-origin',
    },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'copyBoardToOrg',
        operationType: 'mutation',
        operationName: context.operationName,
      },
    },
  );
  const copy = await response.json();

  const searchParams = new URLSearchParams();

  searchParams.set('token', context.token || token || '');
  searchParams.set('name', copy.name);
  searchParams.set('idOrganization', args.organizationId);
  searchParams.set('idBoardSource', args.boardId);
  searchParams.set('creationMethod', 'assisted');
  searchParams.set('keepFromSource', 'cards');
  searchParams.set('prefs_permissionLevel', 'org');

  response = await fetch(networkClient.getUrl(`/1/boards/`), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: searchParams,
  });

  const board = await response.json();

  return prepareDataForApolloCache(board, rootNode);
};

export const addMemberToOrg: TrelloRestResolver<MutationAddMemberToOrgArgs> = async (
  obj,
  { orgId, user, invitationMessage, type = 'normal', traceId = '' },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/organizations/${orgId}/members${user?.id ? `/${user.id}` : ''}`,
    ),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        'X-Trello-TraceId': traceId,
      },
      body: JSON.stringify({
        ...(user?.id ? { acceptUnconfirmed: true } : { email: user?.email }),
        type,
        invitationMessage,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const removeMemberFromWorkspace: TrelloRestResolver<MutationRemoveMemberFromWorkspaceArgs> = async (
  obj,
  { orgId, user, type = 'normal', traceId = '' },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/organizations/${orgId}/members${user?.id ? `/${user.id}` : ''}`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        'X-Trello-TraceId': traceId,
      },
      body: JSON.stringify({
        ...(user?.id ? { acceptUnconfirmed: true } : { email: user?.email }),
        type,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const uploadOrganizationImage: TrelloRestResolver<MutationUploadOrganizationImageArgs> = async (
  obj,
  { orgId, file },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const formData = new FormData();
  formData.set('file', file);
  formData.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  // We need to use XHR in order to track upload progress
  const request = new Promise<JSONObject>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;

    xhr.open('POST', networkClient.getUrl(`/1/organizations/${orgId}/logo`));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.response);
        resolve(response);
      } else {
        reject(xhr.response);
      }
    };
    xhr.onerror = () => {
      reject(xhr.response);
    };

    xhr.send(formData);
  });

  try {
    const organization = await request;
    return prepareDataForApolloCache(organization, rootNode);
  } catch (err) {
    let error: { message: string; error: string };
    try {
      error = JSON.parse(err as string);
    } catch (e) {
      error = {
        message: err as string,
        error: 'UNKNOWN_ERROR',
      };
    }

    throw new NetworkError(error?.message, {
      code: (error?.error as ErrorExtensionsType) || 'UNKNOWN_ERROR',
      status: 400,
    });
  }
};

export const deleteOrganizationLogo: TrelloRestResolver<MutationDeleteOrganizationLogoArgs> = async (
  obj,
  { orgId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/organizations/${orgId}/logo`);
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  // Re-fetching to populate Apollo cache
  const organization = await trelloFetch(
    networkClient.getUrl(`/1/organizations/${orgId}`),
    undefined,
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'deleteOrganizationLogo',
        operationType: 'mutation',
        operationName: context.operationName,
      },
    },
  );
  return prepareDataForApolloCache(await organization.json(), rootNode);
};

export const toggleAutoUpgrade: TrelloRestResolver<MutationToggleAutoUpgradeArgs> = async (
  obj,
  { upgrade, orgId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${orgId}/freeTrial/setAutoUpgrade`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
      upgrade,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const data = await response.json();
  return prepareDataForApolloCache(data, rootNode);
};

export const addFreeTrial: TrelloRestResolver<MutationAddFreeTrialArgs> = async (
  obj,
  { orgId, via, count, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/organizations/${orgId}/freeTrial`);
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
      'X-Trello-TraceId': traceId,
    },
    body: JSON.stringify({
      token: context.token || token,
      ...(via ? { via } : {}),
      ...(count ? { count } : {}),
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const data = await confirmPaidAccount(
    networkClient.getUrl(
      `/1/organizations/${orgId}?fields=credits&paidAccount=true&paidAccount_fields=all`,
    ),
    (model) =>
      isActive(model.paidAccount) &&
      model.paidAccount.trialType === 'freeTrial' &&
      Date.parse(model.paidAccount.trialExpiration) - Date.now() > 0,
    context,
  );

  return prepareDataForApolloCache(data, rootNode);
};

export const applyBCDiscount: TrelloRestResolver<MutationApplyBcDiscountArgs> = async (
  obj,
  { orgId },
  context,
) => {
  const networkClient = getNetworkClient();
  const res = await fetch(
    networkClient.getUrl(`/1/organizations/${orgId}/promotions/redeem`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        promotion: 'bcWinback',
      }),
    },
  );

  if (!res.ok) {
    const error = await res.text();
    throw new Error(error);
  }

  return true;
};

export const organizationCardsResolver: TrelloRestResolver<OrganizationCardsArgs> = async (
  organization: {
    id: string;
  },
  args,
  context,
  info,
): Promise<TypedJSONObject> => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  try {
    const {
      idBoards,
      limit,
      cursor,
      due,
      start,
      date,
      dueComplete,
      sortBy,
      idLists,
      labels,
      idMembers,
    } = args;
    const params = new URLSearchParams({
      idBoards: idBoards.join(','),
      ...(limit && { limit: `${limit}` }),
      ...(cursor && { cursor }),
      ...(start && { start }),
      ...(date && { date }),
      ...(due && { due }),
      ...(dueComplete !== null && { dueComplete: `${dueComplete}` }),
      ...(sortBy && { sortBy }),
      ...(idLists && { idLists: idLists.join(',') }),
      ...(labels && { labels: labels.join(',') }),
      ...(idMembers && { idMembers: idMembers.join(',') }),
    });

    const isPostCardsEnabled = featureFlagClient.get(
      'panorama.post-cards-logic',
      false,
    );

    const networkClient = getNetworkClient();

    const apiUrl = isPostCardsEnabled
      ? networkClient.getUrl(`/1/organizations/${organization.id}/cards`)
      : networkClient.getUrl(
          `/1/organizations/${organization.id}/cards?${params.toString()}`,
        );
    const response = isPostCardsEnabled
      ? await trelloFetch(
          apiUrl,
          {
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify({
              token: context.token || token,
              ...Object.fromEntries(params),
            }),
          },
          {
            clientVersion: context.clientAwareness.version,
            deduplicate: true, //the reason we dedupe here is despite this being a POST request we are only using it to fetch data and consider it to be idempotent for our purposes
            networkRequestEventAttributes: {
              source: 'graphql',
              resolver: 'Organization.cards',
              operationName: context.operationName,
            },
          },
        )
      : await trelloFetch(apiUrl, undefined, {
          clientVersion: context.clientAwareness.version,
          networkRequestEventAttributes: {
            source: 'graphql',
            resolver: 'Organization.cards',
            operationName: context.operationName,
          },
        });

    if (response.ok) {
      model = await response.json();
    } else {
      if ([400, 401, 404, 422, 449].includes(response.status)) {
        model = null;
        throw new Error(await response.text());
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, rootNode, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    throw new Error((err as Error).toString());
  }
};

// Very specific resolver to use on /members/id/cards
export const organizationMemberCardsResolver: TrelloRestResolver<QueryOrganizationMemberCardsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { id, idMember } = args;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organization/${id}/members/${idMember}/cards`,
  );

  const params = new URLSearchParams();
  params.set(
    'fields',
    'badges,closed,dateLastActivity,due,dueComplete,idAttachmentCover,idList,idBoard,idMembers,idShort,labels,name,url',
  );
  params.set('board', 'true');
  params.set('board_fields', 'name,closed,memberships');
  params.set('list', 'true');
  params.set('attachments', 'true');
  params.set('stickers', 'all');
  params.set('members', 'true');
  params.set(
    'member_fields',
    'activityBlocked,avatarUrl,bio,bioData,confirmed,fullName,idEnterprise,idMemberReferrer,initials,memberType,nonPublic,products,url,username',
  );

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'organizationMemberCards',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, rootNode);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

export const addTag: TrelloRestResolver<MutationAddTagArgs> = async (
  obj,
  { orgId, tag },
  context,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/organizations/${orgId}/tags`);
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
      name: tag,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return true;
};

export const bulkDisableWorkspacePowerUp: TrelloRestResolver<MutationBulkDisableWorkspacePowerUpArgs> = async (
  obj,
  { orgId, pluginId, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/organizations/${orgId}/plugins/${pluginId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        'X-Trello-TraceId': traceId,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(traceId, trelloServerVersion);

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const json = await response.json();

  // Since this request could be potentially updating
  // many boards, it may be updated through a heartbeat
  // rather than through this request
  if (json.updateQueued) {
    return json;
  }

  return prepareDataForApolloCache({ plugins: json }, rootNode);
};

export const bulkEnableWorkspacePowerUp: TrelloRestResolver<MutationBulkEnableWorkspacePowerUpArgs> = async (
  obj,
  { orgId, pluginId, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/organizations/${orgId}/plugins/${pluginId}`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        'X-Trello-TraceId': traceId,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(traceId, trelloServerVersion);

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const json = await response.json();

  // Since this request could be potentially updating
  // many boards, it may be updated through a heartbeat
  // rather than through this request
  if (json.updateQueued) {
    return json;
  }

  return prepareDataForApolloCache({ plugins: json }, rootNode);
};

export const createOrganizationInviteSecret: TrelloRestResolver<MutationCreateOrganizationInviteSecretArgs> = async (
  obj,
  { idOrganization, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const operationNameInUrl = featureFlagClient.get(
    'fep.operation-name-in-url',
    false,
  );
  const queryString = operationNameInUrl
    ? `?operationName=${context.operationName}`
    : '';
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${idOrganization}/invitationSecret${queryString}`,
  );

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
  };

  if (traceId) {
    headers['X-Trello-TraceId'] = traceId;
  }

  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const deleteOrganizationInviteSecret: TrelloRestResolver<MutationDeleteOrganizationInviteSecretArgs> = async (
  obj,
  { idOrganization, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const operationNameInUrl = featureFlagClient.get(
    'fep.operation-name-in-url',
    false,
  );
  const queryString = operationNameInUrl
    ? `?operationName=${context.operationName}`
    : '';
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${idOrganization}/invitationSecret${queryString}`,
  );

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
    'X-Trello-TraceId': traceId,
  };

  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const organizationInviteSecretResolver: TrelloRestResolver<QueryOrganizationInviteSecretArgs> = async (
  obj,
  { idOrganization },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/organizations/${idOrganization}/invitationSecret`,
  );

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Organization.invite',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};
